import { Checkbox } from '@chakra-ui/react';
import { t } from 'i18next';
import { ReactNode, useCallback, useMemo } from 'react';
import {
  Column,
  Row,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { SubscriptionTagV2 } from '~components/ui/SubscriptionTagV2';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { getCustomerSubscriptionTagVariant } from '~utils/subscriptions';
import { SubscriptionCustomersTableV2CustomerFragment as CustomerFragment } from './__generated__/SubscriptionCustomersTable.graphql';
import { Columns, SelectableSubscriptionCustomersTableV2CustomerFragment } from './types';

const TableCell = generateTableCellComponent<
  SelectableSubscriptionCustomersTableV2CustomerFragment,
  ReactNode
>();

const TableCellBold = generateTableCellComponent<
  SelectableSubscriptionCustomersTableV2CustomerFragment,
  ReactNode
>({
  isBold: true,
});

export function useSubscriptionCustomersTableV2(
  data: CustomerFragment[],
  currentCustomerIds: string[],
  isActive: boolean,
) {
  const columns = useMemo<Array<Column<SelectableSubscriptionCustomersTableV2CustomerFragment>>>(
    () => [
      {
        id: Columns.Selection,
        Cell: ({ row }: { row: Row<SelectableSubscriptionCustomersTableV2CustomerFragment> }) => {
          const { checked, indeterminate, onChange, role, title } = row.getToggleRowSelectedProps();

          /**
           * A customer is disabled if:
           * - The customer is not already linked to the subscription and the subscription is an essential subscription
           * OR
           * - The customers display count is greater than the available seats and the customer is not checked
           */
          const isDisabled = !isActive && !row.original.isLinkedToSubscription;
          return (
            <StopClickPropagation>
              <Checkbox
                isDisabled={isDisabled}
                isChecked={checked}
                isIndeterminate={indeterminate}
                onChange={onChange}
                role={role}
                title={title}
              />
            </StopClickPropagation>
          );
        },
        width: '48px',
        sortType: (rowA, rowB) => {
          return (
            Number(rowA.original.isLinkedToSubscription) -
            Number(rowB.original.isLinkedToSubscription)
          );
        },
      },
      {
        id: Columns.Name,
        Header: t('customer'),
        accessor: 'name',
        Cell: TableCellBold,
        sortType: (rowA, rowB) =>
          rowA.original.name.toLowerCase().localeCompare(rowB.original.name.toLowerCase()),
        width: 'auto',
      },
      {
        id: Columns.Subscription,
        Header: t('subscription'),
        Cell: ({ row }: { row: Row<SelectableSubscriptionCustomersTableV2CustomerFragment> }) => {
          const variant = getCustomerSubscriptionTagVariant(
            row.original.waveSubscriptions,
            row.original.displayCount,
            row.original.waveSubscriptionUsage,
          );

          return <SubscriptionTagV2 variant={variant} />;
        },
        width: '100px',
      },
      {
        id: Columns.Displays,
        Header: t('Displays'),
        accessor: 'displayCount',
        Cell: TableCell,
        width: '100px',
      },
    ],
    [isActive],
  );

  // The react-table docs specify that this *must* be memoized
  // Because the `useSortBy` plugin for `react-table` is executed _before_ `useRowSelect`, we don't have access to `isSelected` when sorting is applied.
  // We do want to sort on selection state, however, so down below, where we memoize the data, we will be adding that property manually
  const dataMemoized = useMemo<SelectableSubscriptionCustomersTableV2CustomerFragment[]>(
    () =>
      data.map((customer) => ({
        ...customer,
        isLinkedToSubscription: currentCustomerIds.includes(customer.id),
      })),
    [data, currentCustomerIds],
  );

  // The react-table docs specify that this *must* be memoized
  const getRowId = useCallback((row: CustomerFragment) => row.id, []);

  const selectedRowIds = useMemo(
    () =>
      currentCustomerIds.reduce<Record<string, boolean>>((map, current) => {
        map[current] = true;
        return map;
      }, {}),
    [currentCustomerIds],
  );

  return useTable(
    {
      columns,
      data: dataMemoized,
      initialState: {
        pageSize: 15,
        sortBy: [{ id: Columns.Selection, desc: true }, { id: Columns.Name }],
        selectedRowIds,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
      getRowId,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );
}
